// NewEarnedAchievementCelebration — the "Kazanılan yeni başarım" reveal shown inside BaseModal when the
// gamification websocket announces newly-earned achievement(s). A celebratory hero (glow ring + shine
// sweep + pop-in), a confetti burst, an achievement line, and a clean 5-tier ladder with the just-earned
// tier flagged. Multiple achievements page through as a carousel. Prefix: nbc- (unchanged).
export const newEarnedAchievementCelebrationStyles = `
  .nbc {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-5);
    /* Bottom padding clears the footer CTA's downward glow/shadow: .nbc is
       overflow:hidden (to contain the confetti burst), so with no bottom room the
       PrimaryButton's 0 10px 24px shadow (0 12px 30px on hover) is clipped right at
       its bottom edge — reads as the "Harika!" button being cut off ("kesik"). */
    padding: var(--space-2) 0 var(--space-5);
    font-family: var(--font-sans);
    overflow: hidden;
  }

  /* ---- confetti burst (re-keyed per achievement) ---- */
  .nbc-confetti {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    pointer-events: none;
    overflow: hidden;
    z-index: 2;
  }
  .nbc-confetti__piece {
    position: absolute;
    top: -24px;
    display: block;
    opacity: 0;
    will-change: transform, opacity;
    animation-name: nbc-confetti-fall;
    animation-timing-function: cubic-bezier(0.22, 0.61, 0.36, 1);
    animation-iteration-count: 1;
    animation-fill-mode: both;
  }
  @keyframes nbc-confetti-fall {
    0% { opacity: 0; transform: translate3d(0, -10px, 0) rotate(0deg) scale(0.6); }
    12% { opacity: 1; }
    100% {
      opacity: 0;
      transform: translate3d(var(--nbc-dx, 0), 340px, 0) rotate(var(--nbc-rot, 360deg)) scale(1);
    }
  }

  /* ---- hero achievement ---- */
  .nbc-stage {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-5);
    width: 100%;
    animation: nbc-stage-in var(--duration-slow) var(--ease-glass) both;
  }
  @keyframes nbc-stage-in {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: none; }
  }
  .nbc-hero {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 312px;
  }
  .nbc-hero__glow {
    position: absolute;
    width: 340px;
    height: 340px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(115, 215, 230, 0.5) 0%,
      rgba(115, 215, 230, 0.2) 44%,
      rgba(115, 215, 230, 0) 70%
    );
    animation: nbc-glow-pulse 2.6s var(--ease-standard) infinite;
  }
  @keyframes nbc-glow-pulse {
    0%, 100% { transform: scale(0.92); opacity: 0.7; }
    50% { transform: scale(1.06); opacity: 1; }
  }
  /* Dashed halo — sized LARGER than the ~200px achievement tile so it encircles it in full view. */
  .nbc-hero__ring {
    position: absolute;
    width: 288px;
    height: 288px;
    border-radius: 50%;
    border: 2.5px dashed rgba(87, 194, 211, 0.5);
    animation: nbc-ring-spin 22s linear infinite;
  }
  /* A bright arc that sweeps around the achievement tile — the clearly-"turning" element. Conic gradient masked
     into a thin ring band; falls back gracefully to just the dashed halo where mask is unsupported. */
  .nbc-hero__comet {
    position: absolute;
    width: 272px;
    height: 272px;
    border-radius: 50%;
    background: conic-gradient(
      from 0deg,
      rgba(87, 194, 211, 0) 0deg,
      rgba(87, 194, 211, 0) 200deg,
      rgba(87, 194, 211, 0.9) 320deg,
      rgba(115, 215, 230, 0) 360deg
    );
    -webkit-mask: radial-gradient(
      circle,
      transparent 0 45%,
      #000 46.5% 49.5%,
      transparent 51%
    );
    mask: radial-gradient(
      circle,
      transparent 0 45%,
      #000 46.5% 49.5%,
      transparent 51%
    );
    animation: nbc-ring-spin 3.4s linear infinite;
  }
  @keyframes nbc-ring-spin { to { transform: rotate(360deg); } }
  .nbc-hero__tile {
    position: relative;
    z-index: 1;
    overflow: hidden;
    border-radius: var(--rad-catchup-large);
    animation: nbc-achievement-pop 700ms var(--ease-glass) both;
  }
  @keyframes nbc-achievement-pop {
    0% { opacity: 0; transform: scale(0.3) rotate(-14deg); }
    55% { opacity: 1; transform: scale(1.12) rotate(4deg); }
    75% { transform: scale(0.97) rotate(-1deg); }
    100% { opacity: 1; transform: scale(1) rotate(0deg); }
  }
  .nbc-hero__shine {
    position: absolute;
    top: 0;
    left: -60%;
    width: 55%;
    height: 100%;
    pointer-events: none;
    background: linear-gradient(
      100deg,
      transparent 0%,
      rgba(255, 255, 255, 0.75) 50%,
      transparent 100%
    );
    transform: skewX(-18deg);
    animation: nbc-shine-sweep 1.6s var(--ease-standard) 0.5s 1;
  }
  @keyframes nbc-shine-sweep {
    0% { left: -60%; opacity: 0; }
    30% { opacity: 1; }
    100% { left: 130%; opacity: 0; }
  }

  /* ---- info block ---- */
  .nbc-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-4);
    width: 100%;
    max-width: 560px;
    text-align: center;
  }
  .nbc-chips {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: var(--space-2);
  }
  .nbc-chip {
    display: inline-flex;
    align-items: center;
    font-size: var(--fs-sm);
    font-weight: 800;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    padding: 5px 14px;
    border-radius: var(--rad-catchup-full);
  }
  .nbc-chip--subject {
    color: var(--catchup-purple);
    background: rgba(99, 3, 48, 0.08);
  }
  .nbc-chip--level {
    color: var(--accent-gold-text);
    background: var(--accent-gold-bg);
    border: 1px solid var(--accent-gold-border);
  }
  .nbc-rule {
    font-size: var(--fs-xl);
    font-weight: 700;
    line-height: 1.35;
    color: var(--text-primary);
  }

  /* ---- 5-tier ladder ---- */
  .nbc-ladder {
    position: relative;
    width: 100%;
    padding-top: var(--space-4);
  }
  .nbc-ladder__track {
    position: absolute;
    top: 34px;
    left: 10%;
    right: 10%;
    height: 6px;
    border-radius: 99px;
    background: var(--catchup-gray-100);
    overflow: hidden;
    z-index: 0;
  }
  .nbc-ladder__track span {
    display: block;
    height: 100%;
    border-radius: 99px;
    background: linear-gradient(90deg, var(--brand-light), var(--brand-dark));
    transition: width var(--duration-slow) var(--ease-standard);
  }
  .nbc-ladder__tiles {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-2);
  }
  .nbc-tier {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1 1 0;
    min-width: 0;
    padding: 6px 0;
    border-radius: var(--rad-catchup-large);
    transition: transform var(--duration-base) var(--ease-standard);
  }
  .nbc-tier.is-new {
    background: var(--glass-flat-bg);
    -webkit-backdrop-filter: var(--glass-flat-filter);
    backdrop-filter: var(--glass-flat-filter);
    box-shadow: var(--glass-flat-edge), 0 0 0 2px var(--brand),
      0 10px 26px -12px rgba(87, 194, 211, 0.65);
    transform: translateY(-4px);
    animation: nbc-tier-pop 700ms var(--ease-glass) 0.2s both;
  }
  @keyframes nbc-tier-pop {
    0% { transform: translateY(-4px) scale(0.9); }
    60% { transform: translateY(-4px) scale(1.06); }
    100% { transform: translateY(-4px) scale(1); }
  }
  .nbc-tier__tag {
    position: absolute;
    top: -8px;
    font-size: 9px;
    font-weight: 900;
    letter-spacing: 0.6px;
    color: #fff;
    background: var(--catchup-blue);
    padding: 2px 8px;
    border-radius: var(--rad-catchup-full);
    box-shadow: 0 4px 10px -3px rgba(87, 194, 211, 0.7);
    z-index: 2;
  }
  .nbc-tier__tick {
    position: absolute;
    bottom: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--catchup-green);
    color: #fff;
    font-size: 10px;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px -2px rgba(120, 156, 35, 0.6);
  }

  /* ---- footer / carousel ---- */
  .nbc-foot {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-4);
    width: 100%;
    padding-top: var(--space-2);
  }
  .nbc-nav {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    font-weight: 700;
    color: var(--brand-dark);
    cursor: pointer;
    border-radius: var(--rad-catchup-full);
    background: var(--glass-flat-bg);
    -webkit-backdrop-filter: var(--glass-flat-filter);
    backdrop-filter: var(--glass-flat-filter);
    border: 1px solid var(--glass-flat-border);
    box-shadow: var(--glass-flat-edge);
    transition: all var(--duration-base) var(--ease-standard);
  }
  .nbc-nav:hover:not(:disabled) {
    background: var(--glass-active-bg);
    transform: translateX(-2px);
  }
  .nbc-nav:disabled { opacity: 0.35; cursor: not-allowed; }
  .nbc-dots {
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .nbc-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--catchup-gray-100);
    transition: all var(--duration-base) var(--ease-standard);
  }
  .nbc-dot.is-on {
    width: 22px;
    border-radius: 99px;
    background: var(--catchup-blue);
  }
  .nbc-count {
    font-size: var(--fs-base);
    font-weight: 700;
    color: var(--text-muted);
    min-width: 44px;
    text-align: center;
  }
  /* The footer CTA is the shared library PrimaryButton (frosted-glass pill);
     the old bespoke solid-teal .nbc-cta button was retired in favour of it. */

  @media (max-width: 620px) {
    .nbc-hero { min-height: 176px; }
    .nbc-hero__glow { width: 240px; height: 240px; }
    .nbc-hero__ring { width: 156px; height: 156px; }
    .nbc-rule { font-size: var(--fs-lg); }
  }

  @media (prefers-reduced-motion: reduce) {
    .nbc-confetti { display: none; }
    .nbc-stage,
    .nbc-hero__tile,
    .nbc-hero__shine,
    .nbc-hero__glow,
    .nbc-hero__ring,
    .nbc-hero__comet,
    .nbc-tier.is-new { animation: none; }
  }
`;
